import { useEffect, useRef, type RefObject } from "react";

export type ChatFindTarget = {
  itemId: string;
  requestId: number;
  query: string;
  occurrenceIndex: number;
};

type FindNavigationOptions = {
  scrollRef: RefObject<HTMLDivElement>;
  query: string;
  target?: ChatFindTarget;
  findRowIndex: (itemId: string) => number;
  scrollToRow: (index: number) => void;
  expandItem: (itemId: string) => void;
  onNavigate: () => void;
  onVisibleMatchCount?: (itemId: string, count: number, query: string) => void;
};

function textRanges(element: Element, query: string): Range[] {
  if (!query) return [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const nodes: Array<{ node: Text; start: number; end: number }> = [];
  let text = "";
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const parent = node.parentElement;
    if (
      !parent ||
      parent.closest('[aria-hidden="true"], [hidden], script, style')
    )
      continue;
    if (!parent.getClientRects().length) continue;
    nodes.push({ node, start: text.length, end: text.length + node.length });
    text += node.data;
  }
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  const ranges: Range[] = [];
  let offset = haystack.indexOf(needle);
  while (offset !== -1 && ranges.length < 2_000) {
    const start = nodes.find((node) => node.end > offset);
    const endOffset = offset + needle.length;
    const end = nodes.find((node) => node.end >= endOffset);
    if (start && end) {
      const range = document.createRange();
      range.setStart(start.node, offset - start.start);
      range.setEnd(end.node, endOffset - end.start);
      ranges.push(range);
    }
    offset = haystack.indexOf(needle, offset + needle.length);
  }
  return ranges;
}

function matchingRanges(element: Element, query: string): Range[] {
  const markedContent = [
    ...element.querySelectorAll("[data-chat-search-content]"),
  ];
  const searchRoots = markedContent.length ? markedContent : [element];
  return searchRoots
    .flatMap((searchRoot) => textRanges(searchRoot, query))
    .slice(0, 2_000);
}

function itemElement(root: Element, itemId: string) {
  return root.querySelector(`[data-chat-item-id="${CSS.escape(itemId)}"]`);
}

export function useChatFindNavigation({
  scrollRef,
  query,
  target,
  findRowIndex,
  scrollToRow,
  expandItem,
  onNavigate,
  onVisibleMatchCount,
}: FindNavigationOptions) {
  const callbacks = useRef({
    findRowIndex,
    scrollToRow,
    expandItem,
    onNavigate,
    onVisibleMatchCount,
  });
  callbacks.current = {
    findRowIndex,
    scrollToRow,
    expandItem,
    onNavigate,
    onVisibleMatchCount,
  };
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof Highlight === "undefined") return;
    let frame: number | undefined;
    const refresh = () => {
      const ranges = Array.from(
        root.querySelectorAll("[data-chat-item-id]"),
      ).flatMap((element) => matchingRanges(element, query));
      CSS.highlights.set("lawworks-find", new Highlight(...ranges));
      const selection = targetRef.current;
      const element = selection && itemElement(root, selection.itemId);
      const matches = element ? matchingRanges(element, query) : [];
      const current =
        selection && selection.query === query
          ? matches[selection.occurrenceIndex]
          : undefined;
      CSS.highlights.set(
        "lawworks-find-current",
        new Highlight(...(current ? [current] : [])),
      );
    };
    const schedule = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(refresh);
    };
    refresh();
    const observer = new MutationObserver(schedule);
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
    });
    return () => {
      observer.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
      CSS.highlights.delete("lawworks-find");
      CSS.highlights.delete("lawworks-find-current");
    };
  }, [query, scrollRef, target?.requestId]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !target) return;
    const navigation = callbacks.current;
    const rowIndex = navigation.findRowIndex(target.itemId);
    if (rowIndex < 0) return;
    navigation.onNavigate();
    navigation.expandItem(target.itemId);
    let frame: number;
    let reportFrame: number | undefined;
    let attempts = 0;
    let reportedVisibleCount = false;
    // A nearby rendered result can scroll directly to the match. A virtualized
    // result first scrolls its row into the window without loading other pages.
    if (!itemElement(root, target.itemId)) navigation.scrollToRow(rowIndex);
    const focusMatch = () => {
      const element = itemElement(root, target.itemId);
      const matches = element ? matchingRanges(element, target.query) : [];
      if (element && !reportedVisibleCount) {
        reportedVisibleCount = true;
        reportFrame = requestAnimationFrame(() => {
          reportFrame = requestAnimationFrame(() => {
            const settledElement = itemElement(root, target.itemId);
            const settledCount = settledElement
              ? matchingRanges(settledElement, target.query).length
              : 0;
            navigation.onVisibleMatchCount?.(
              target.itemId,
              settledCount,
              target.query,
            );
          });
        });
      }
      const match = matches.at(target.occurrenceIndex);
      if (element && (match || attempts > 30)) {
        match?.startContainer.parentElement?.scrollIntoView({
          block: "nearest",
        });
        const rect =
          match?.getBoundingClientRect() ?? element.getBoundingClientRect();
        const viewport = root.getBoundingClientRect();
        root.scrollTo({
          top:
            root.scrollTop +
            rect.top -
            viewport.top -
            (root.clientHeight - rect.height) / 2,
          behavior: "smooth",
        });
        if (typeof Highlight !== "undefined") {
          CSS.highlights.set(
            "lawworks-find-current",
            new Highlight(...(match ? [match] : [])),
          );
        }
        return;
      }
      if (++attempts < 120) frame = requestAnimationFrame(focusMatch);
    };
    frame = requestAnimationFrame(focusMatch);
    return () => {
      cancelAnimationFrame(frame);
      if (reportFrame !== undefined) cancelAnimationFrame(reportFrame);
    };
  }, [scrollRef, target]);
}
